import {AttachmentType} from '../../../backend/enums/attachment-type';
import {AttachmentWithFile} from '../../../backend/interfaces/attachment';
import {
  ActionHistory,
  CareTaskChangesHistory,
  ChangeHistoryValues,
} from '../../../backend/interfaces/care-task';

/**
 * This function converts legacy action history to the new format.
 */
export function convertLegacyHistoryToNewHistory(
  legacyHistory?: ActionHistory[]
): CareTaskChangesHistory[] | undefined {
  // Skip
  if (!legacyHistory || legacyHistory.length === 0) {
    return undefined;
  }

  // Initialize
  let resultMap = new Map<string, CareTaskChangesHistory>();

  // Iterate & Convert
  for (const action of legacyHistory) {
    // We only support some action types for conversion.
    if (
      action.actionType !== 'partial-update' &&
      action.actionType !== 'remove' &&
      // @ts-ignore
      action.actionType !== 'notes-updated' &&
      // @ts-ignore
      action.actionType !== 'attachment-removed' &&
      // @ts-ignore
      action.actionType !== 'attachment-added'
    ) {
      continue;
    }

    let changes: ChangeHistoryValues[] = [];

    // A. If it's a 'partial-update' legacy action, we only care about the changes bellow.
    if (action.actionType === 'partial-update') {
      if (!isNil(action.markedAsDone)) {
        changes.push({
          field: 'markedAsDone',
          oldValue: undefined,
          newValue: action.markedAsDone,
        });
      }

      if (!isNil(action.taskNotDoneReason)) {
        changes.push({
          field: 'taskNotDoneReason',
          oldValue: undefined,
          newValue: action.taskNotDoneReason,
        });
      }
    }
    // B. It's not a 'partial-update' legacy action, so we handle the changes bellow.
    else {
      if (!isNil(action.attachments) && action.attachments!.length > 0) {
        changes.push({
          field: 'attachments',
          oldValue: [],
          newValue: action.attachments,
        });
      }

      if (!isNil(action.notes)) {
        // Edge Case: the attachment is in the notes field.
        // @ts-ignore
        if (action.actionType === 'attachment-removed') {
          const value: AttachmentWithFile[] = [
            {
              type: AttachmentType.Document, // we can't tell the type, so we assume Document
              data: {
                fileUrl: action.notes as string,
              },
            },
          ];

          changes.push({
            field: 'attachments',
            oldValue: value,
            newValue: [],
          });
        }
        // Edge Case: the attachment is in the notes field.
        // @ts-ignore
        else if (action.actionType === 'attachment-added') {
          const value: AttachmentWithFile[] = [
            {
              type: AttachmentType.Document, // we can't tell the type, so we assume Document
              data: {
                fileUrl: action.notes as string,
              },
            },
          ];

          changes.push({
            field: 'attachments',
            oldValue: [],
            newValue: value,
          });
        }
        // Normal case for notes update
        else {
          changes.push({
            field: 'notes',
            oldValue: undefined,
            newValue: action.notes,
          });
        }
      }
    }

    // This is actually a ISO string not a Date obj.
    const registeredAt = action.registeredAt as unknown as string;

    if (resultMap.has(registeredAt)) {
      // If we already have a history for this timestamp, we merge the changes.
      let existingHistory = resultMap.get(registeredAt);
      if (existingHistory) {
        existingHistory.changes = (existingHistory.changes ?? []).concat(changes);
        continue;
      }
    }

    let newHistory: CareTaskChangesHistory = {
      changeType: action.actionType === 'remove' ? 'remove' : 'partial-update',
      registeredAt: registeredAt,
      userAuthorId: action.userAuthorId,
      externalSystemAuthorId: action.externalSystemAuthorId,
      changes: changes.length > 0 ? changes : undefined,
    };

    resultMap.set(newHistory.registeredAt, newHistory);
  }

  return Array.from(resultMap.values());
}

/** nil: A value that is `undefined` OR `null` */
function isNil(value: any): boolean {
  return value === undefined || value === null;
}
